import { PlaybackControls } from '@/components/admin/npc/watch/playback-controls';
import { WatchBar } from '@/components/admin/npc/watch/watch-bar';
import { WatchScreen } from '@/components/admin/npc/watch/watch-screen';
import { useAdminSession } from '@/lib/admin-session';
import { fetchReplayPlayback, fetchReplayWatch } from '@/lib/api';
import {
  advanceClock,
  frameAt,
  nextPageAt,
  pageMarks,
  playbackLength,
  timedSteps,
} from '@/lib/npc-playback';
import {
  playbackPositionLabel,
  watchErrorSentence,
  watchExperienceLabel,
  watchStateLabel,
  watchTurnLabel,
} from '@/lib/npc-watch';
import type { NpcPlayback, NpcPlaybackSpeed, NpcWatch } from '@matter_extractor/shared';
import { NPC_WATCH_POLL_MS } from '@matter_extractor/shared';
import { Button, Spinner } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

/**
 * « Regarder » un rejeu (npc.md §11, N11, H17 ; maquette `Regarder`, variante A) : l'écran de la
 * personne, dans le thème de l'expérience, en lecture seule, sous une fine barre sombre. La page
 * prend toute la fenêtre, par-dessus le rail, comme la session prend la sienne.
 *
 * ## En direct, puis en différé
 *
 * Un rejeu qui tourne se regarde EN DIRECT : la vue est relue toutes les `NPC_WATCH_POLL_MS`, les
 * pages, les temps et les bulles arrivent comme chez la personne. Quand le serveur dit qu'il n'est
 * plus en direct (fini, fermé, parti, page de retour), le sondage s'arrête, l'écran RESTE sur la
 * dernière page et la barre propose « Revoir en différé ». Un rejeu déjà fini à l'ouverture s'ouvre
 * directement en différé : la suite enregistrée se rejoue à vitesse de lecture (`PlaybackWatch`).
 *
 * Ce qui était en direct à l'ouverture est retenu une fois (`startedLive`), et jamais relu :
 * c'est ce qui distingue « le rejeu vient de finir sous mes yeux » de « j'ouvre un rejeu fini ».
 *
 * ## Les refus
 *
 * Un rejeu purgé dit que sa session n'existe plus (§18) ; jamais une page blanche. La page ne
 * s'ouvre que pour un rejeu : le serveur refuse une vraie session (`not-a-replay`).
 */

export interface NpcWatchPageProps {
  replayId: string;
}

export function NpcWatchPage({ replayId }: NpcWatchPageProps) {
  const { email } = useAdminSession();
  const router = useRouter();
  const navigate = useNavigate();
  const [wantsPlayback, setWantsPlayback] = useState(false);
  const [startedLive, setStartedLive] = useState<boolean | null>(null);

  const watch = useQuery({
    queryKey: ['npc-watch', email, replayId],
    queryFn: () => fetchReplayWatch(replayId),
    retry: false,
    staleTime: 0,
    // A read that failed stops the polling: the screen says why (a purged replay answers 404
    // to every read), and polling a notice forever would only make it flicker.
    refetchInterval: (query) =>
      query.state.status !== 'error' && query.state.data?.live === true ? NPC_WATCH_POLL_MS : false,
  });
  // Seeded ONCE from the first answer, during the render that receives it: no effect repairs it.
  if (startedLive === null && watch.data !== undefined) setStartedLive(watch.data.live);

  const playbackOn = wantsPlayback || startedLive === false;
  const playback = useQuery({
    queryKey: ['npc-playback', email, replayId],
    queryFn: () => fetchReplayPlayback(replayId),
    enabled: playbackOn,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    // The recorded suite of a finished replay does not move: a read on coming back to the tab
    // would hand the player a new object and send the playback back to its start.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const quit = () => {
    if (window.history.length > 1) router.history.back();
    else void navigate({ to: '/admin/npc' });
  };

  let body: React.ReactNode;
  if (watch.isError) {
    body = (
      <Notice sentence={watchErrorSentence(watch.error)} onQuit={quit} testId="regarder-erreur" />
    );
  } else if (playbackOn && playback.isError) {
    body = (
      <Notice
        sentence={watchErrorSentence(playback.error)}
        onQuit={quit}
        testId="regarder-erreur"
      />
    );
  } else if (watch.data === undefined || (playbackOn && playback.data === undefined)) {
    body = (
      <div className="flex flex-1 items-center justify-center">
        <Spinner label="Ouverture du rejeu" />
      </div>
    );
  } else if (playbackOn && playback.data !== undefined) {
    body = <PlaybackWatch playback={playback.data} onQuit={quit} />;
  } else {
    body = <LiveWatch watch={watch.data} onReplay={() => setWantsPlayback(true)} onQuit={quit} />;
  }

  return (
    <div data-testid="regarder" className="fixed inset-0 z-50 flex flex-col bg-white">
      {body}
    </div>
  );
}

/** A replay there is nothing to watch of: its sentence, and the way out. */
function Notice({
  sentence,
  onQuit,
  testId,
}: { sentence: string; onQuit: () => void; testId: string }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
      <p data-testid={testId} className="max-w-[62ch] text-gray-800">
        {sentence}
      </p>
      <div className="flex gap-4 text-small">
        <button
          type="button"
          onClick={onQuit}
          className="cursor-pointer font-semibold text-bleu-nuit underline underline-offset-2"
        >
          Quitter
        </button>
        <Link to="/admin/npc" className="text-bleu-nuit underline underline-offset-2">
          Revenir aux NPC
        </Link>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- en direct */

function LiveWatch({
  watch,
  onReplay,
  onQuit,
}: {
  watch: NpcWatch;
  onReplay: () => void;
  onQuit: () => void;
}) {
  const { header, live, screen } = watch;
  const personName = screen.persona?.kind === 'named' ? screen.persona.firstName : null;
  return (
    <>
      <WatchBar
        npc={header.npc === null ? null : { id: header.npc.id, name: header.npc.name }}
        experience={watchExperienceLabel(header)}
        state={{ label: watchStateLabel(live ? 'live' : 'over', screen.endedAt), live }}
        turn={watchTurnLabel(header.turn)}
        {...(live ? {} : { note: 'Le rejeu est fini' })}
        {...(live
          ? {}
          : {
              actions: (
                // The package's white button: it holds on the dark bar as the hand-drawn one did.
                <Button
                  type="button"
                  variant="primary"
                  size="xs"
                  compact
                  data-testid="regarder-differe"
                  onClick={onReplay}
                >
                  Revoir en différé
                </Button>
              ),
            })}
        onQuit={onQuit}
      />
      <div className="min-h-0 flex-1">
        <WatchScreen
          view={screen}
          note={
            personName === null
              ? 'Le joueur répondra à son tour'
              : `Le joueur répondra quand ${personName} aura fini`
          }
        />
      </div>
    </>
  );
}

/* ------------------------------------------------------------------- en différé */

function PlaybackWatch({ playback, onQuit }: { playback: NpcPlayback; onQuit: () => void }) {
  const timed = useMemo(() => timedSteps(playback.steps), [playback]);
  const length = useMemo(() => playbackLength(timed), [timed]);
  const marks = useMemo(() => pageMarks(timed), [timed]);
  const [at, setAt] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState<NpcPlaybackSpeed>(1);
  const running = playing && at < length;

  // The clock of the playback: a TIMER and not data. It ticks while the recording plays and sleeps
  // on a pause and at the end, so a finished replay costs nothing.
  useEffect(() => {
    if (!running) return;
    let last = performance.now();
    const timer = window.setInterval(() => {
      const now = performance.now();
      const elapsed = now - last;
      last = now;
      setAt((was) => advanceClock(was, elapsed, speed, length));
    }, 100);
    return () => window.clearInterval(timer);
  }, [running, speed, length]);

  const frame = useMemo(() => frameAt(playback, timed, at), [playback, timed, at]);
  const next = nextPageAt(timed, at);
  const { header } = playback;

  return (
    <>
      <WatchBar
        npc={header.npc === null ? null : { id: header.npc.id, name: header.npc.name }}
        experience={watchExperienceLabel(header)}
        state={{ label: watchStateLabel('playback', null), live: false }}
        turn={null}
        onQuit={onQuit}
        below={
          <PlaybackControls
            playing={running}
            speed={speed}
            at={at}
            length={length}
            marks={marks}
            position={playbackPositionLabel(frame)}
            hasNext={next !== null}
            onToggle={() => {
              if (at >= length) {
                setAt(0);
                setPlaying(true);
              } else setPlaying((was) => !was);
            }}
            onSpeed={setSpeed}
            onNext={() => {
              if (next !== null) setAt(next);
            }}
            onSeek={setAt}
          />
        }
      />
      <div className="min-h-0 flex-1">
        <WatchScreen
          view={frame.view}
          {...(frame.revealed === null ? {} : { revealed: frame.revealed })}
          recorded
          note="Votre réponse"
        />
      </div>
    </>
  );
}
